import { useEffect, useState } from "react"
import mixpanel from "mixpanel-browser"
import { omitBy, isUndefined } from "lodash"
import { warn } from "helpers/dev-logger"

const DEFAULT_HOST = "https://api-js.mixpanel.com"
const IS_PRODUCTION = process.env.NODE_ENV === "production"

// Module-level: persists across React unmount/remount so remounts skip re-init.
let isMixpanelReady = false

// [TODO:TS] Placeholder type for event property values, will be replaced with the actual event property value type and properties once determined.
type EventPropertyValue = string | number | boolean | null | undefined

export interface UseMixPanelOptions {
  distinctId: string
  eventProperties?: Record<string, EventPropertyValue>
  sessionSampleRate?: number
  name?: string
  email?: string
}

function readMixpanelToken(): string | null {
  const bundle = document.getElementById("app")?.getAttribute("data-bundle")
  if (!bundle) {
    return null
  }

  try {
    const data = JSON.parse(bundle) as {
      mixpanelToken?: string
    }

    return data.mixpanelToken ?? null
  } catch {
    return null
  }
}

export default function useMixPanel(options: UseMixPanelOptions): void {
  const {
    distinctId = null,
    eventProperties,
    sessionSampleRate = 100,
    name,
    email,
  } = options

  // Seed from module-level flag so a remount starts ready when the singleton already initialized.
  const [mpReady, setMpReady] = useState(isMixpanelReady)

  useEffect(() => {
    if (!IS_PRODUCTION || !distinctId || mpReady) {
      return
    }

    const token = readMixpanelToken()
    if (!token) {
      return
    }

    try {
      // Security: consult the team before changing session recording params — masking prevents sensitive data leaks.
      mixpanel.init(token, {
        api_host: DEFAULT_HOST,
        record_sessions_percent: sessionSampleRate,
        // Session-replay masking — INTENTIONALLY mask-all (fail-closed). "*" masks every text
        // node so customer PII — security names, position sizes, all data-table values — never
        // reaches a replay; inputs are masked by the SDK default (maskAllInputs). Do NOT narrow
        // this to a data-only selector: that fails open, and a single un-tagged table would leak
        // exactly the data we must hide. Selectively un-masking safe OP chrome (nav/labels) for
        // replay readability needs unmask-selector support, which mixpanel-browser@2.73.0 lacks —
        // tracked as a follow-up. NOTE: capture_text_content below is an *autocapture* setting and
        // does NOT affect replay masking. See APP-3222.
        record_mask_text_selector: "*",
        record_block_selector: "img, video, audio",
        autocapture: {
          pageview: "full-url",
          click: true,
          input: true,
          scroll: true,
          submit: true,
          capture_text_content: false,
        },
        // __loaded is not set synchronously — use loaded callback as the readiness gate.
        loaded: () => {
          isMixpanelReady = true
          setMpReady(true)
        },
      })
    } catch (err) {
      warn("[Mixpanel] init failed", err)
    }
  }, [sessionSampleRate, distinctId, mpReady])

  useEffect(() => {
    if (!IS_PRODUCTION || !mpReady || !distinctId) {
      return
    }

    try {
      mixpanel.register(omitBy(eventProperties ?? {}, isUndefined))
    } catch (err) {
      warn("[Mixpanel] register failed", err)
    }
  }, [eventProperties, distinctId, mpReady])

  useEffect(() => {
    if (!IS_PRODUCTION || !mpReady || !distinctId) {
      return
    }

    try {
      mixpanel.identify(distinctId)
      // Set the profile name/email so the replay/user list shows a human name instead of
      // the raw distinct_id (UUID), independent of the Segment identify pipeline. See APP-3222.
      const personProps = omitBy({ $name: name, $email: email }, isUndefined)
      if (Object.keys(personProps).length > 0) {
        mixpanel.people.set(personProps)
      }
    } catch (err) {
      warn("[Mixpanel] identify failed", err)
    }
  }, [distinctId, mpReady, name, email])

  useEffect(() => {
    if (!IS_PRODUCTION || !mpReady || !distinctId) {
      return
    }

    try {
      mixpanel.opt_in_tracking()
      mixpanel.start_session_recording()
    } catch (err) {
      warn("[Mixpanel] start_session_recording failed", err)
    }

    return () => {
      try {
        mixpanel.stop_session_recording()
        mixpanel.opt_out_tracking()
      } catch (err) {
        warn("[Mixpanel] stop_session_recording failed", err)
      }
    }
  }, [distinctId, mpReady])
}
